export interface ResourceSummaryItem {
  id: string;
  title: string;
  icon: string;
  color: string;
  available: boolean;
  viewed: boolean;
}

interface VideoSectionProps {
  vimeoId: string;
  resourceSummary?: ResourceSummaryItem[];
  onGoToResource?: (resourceId: string) => void;
}

export default function VideoSection({ vimeoId, resourceSummary = [], onGoToResource }: VideoSectionProps) {
  const availableResources = resourceSummary.filter((r) => r.available);
  const viewedCount = availableResources.filter((r) => r.viewed).length;
  const allViewed = availableResources.length > 0 && viewedCount === availableResources.length;

  if (!vimeoId) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-5">
        <div className="w-20 h-20 flex items-center justify-center rounded-full" style={{ background: 'rgba(248,246,243,0.05)', border: '1px solid rgba(248,246,243,0.08)' }}>
          <i className="ri-video-off-line text-[#F8F6F3]/25 text-3xl" />
        </div>
        <p className="text-[#F8F6F3]/40 text-sm">El video de este módulo estará disponible próximamente.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Vimeo Player */}
      <div className="lg:col-span-2 flex flex-col gap-4">
        <div
          className="relative rounded-2xl overflow-hidden w-full"
          style={{ aspectRatio: '16/9', background: '#0D0F12' }}
        >
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}?badge=0&autopause=0&player_id=0&app_id=58479&color=8B9D83&title=0&byline=0&portrait=0`}
            className="absolute inset-0 w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            allowFullScreen
            title="Nodo 0 — El Origen"
            style={{ border: 'none' }}
          />
        </div>

        {/* Description */}
        <div className="p-5 rounded-xl" style={{ background: 'rgba(248,246,243,0.04)', border: '1px solid rgba(248,246,243,0.06)' }}>
          <p className="text-[#F8F6F3]/60 text-sm leading-relaxed">
            En este video introductorio exploraremos los fundamentos del sistema Nube Geodésica: qué es la Re-ingeniería Espiritual, cómo funciona la Cartografía del Alma, y cuál es el propósito de los 6 Pilares Sistémicos que guiarán tu transformación. Es el punto de partida de todo tu trazado.
          </p>
        </div>
      </div>

      {/* Right sidebar: only resources */}
      <div className="flex flex-col gap-4 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[3px]">Recursos del módulo</p>
          {availableResources.length > 0 && (
            <span
              className="text-[10px] px-2.5 py-1 rounded-full font-medium"
              style={{
                background: allViewed ? 'rgba(139,157,131,0.15)' : 'rgba(201,123,99,0.12)',
                color: allViewed ? '#8B9D83' : '#C97B63',
              }}
            >
              {viewedCount}/{availableResources.length}
            </span>
          )}
        </div>

        {/* Progress bar */}
        {availableResources.length > 0 && (
          <div className="w-full h-1 rounded-full overflow-hidden" style={{ background: 'rgba(248,246,243,0.06)' }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${(viewedCount / availableResources.length) * 100}%`,
                background: allViewed ? '#8B9D83' : '#C97B63',
              }}
            />
          </div>
        )}

        {/* Resource list */}
        <div className="flex flex-col gap-2">
          {resourceSummary.length === 0 && (
            <p className="text-[#F8F6F3]/25 text-xs py-4 text-center">Sin recursos disponibles aún</p>
          )}

          {resourceSummary.map((r) => (
            <button
              key={r.id}
              onClick={() => r.available && onGoToResource?.(r.id)}
              disabled={!r.available}
              className="flex items-center gap-3 p-3.5 rounded-xl text-left transition-all duration-200 w-full group"
              style={{
                background: 'rgba(248,246,243,0.03)',
                border: '1px solid rgba(248,246,243,0.06)',
                opacity: r.available ? 1 : 0.40,
                cursor: r.available ? 'pointer' : 'default',
              }}
            >
              <div
                className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                style={{ background: `${r.color}12`, border: `1px solid ${r.color}22` }}
              >
                <i className={`${r.icon} text-sm`} style={{ color: r.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-xs font-medium truncate transition-colors duration-300"
                  style={{
                    color: !r.available
                      ? 'rgba(248,246,243,0.30)'
                      : r.viewed
                      ? '#8B9D83'
                      : '#C97B63',
                  }}
                >
                  {r.title}
                </p>
                <p className="text-[10px] mt-0.5" style={{ color: 'rgba(248,246,243,0.25)' }}>
                  {!r.available ? 'Próximamente' : r.viewed ? 'Consultado' : 'Sin abrir'}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{
                    background: !r.available ? 'rgba(248,246,243,0.12)' : r.viewed ? '#8B9D83' : '#C97B63',
                    boxShadow: r.available ? `0 0 5px ${r.viewed ? 'rgba(139,157,131,0.5)' : 'rgba(201,123,99,0.5)'}` : 'none',
                  }}
                />
                {r.available && (
                  <i className="ri-arrow-right-s-line text-[#F8F6F3]/20 text-sm group-hover:text-[#F8F6F3]/50 transition-colors" />
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Completion badge */}
        <div className="mt-auto pt-2">
          <div
            className="p-4 rounded-xl"
            style={{
              background: allViewed ? 'rgba(139,157,131,0.08)' : 'rgba(201,123,99,0.08)',
              border: `1px solid ${allViewed ? 'rgba(139,157,131,0.15)' : 'rgba(201,123,99,0.15)'}`,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${allViewed ? 'ri-checkbox-circle-line text-[#8B9D83]' : 'ri-award-line text-[#C97B63]'} text-sm`} />
              </div>
              <span
                className="text-[10px] uppercase tracking-[2px]"
                style={{ color: allViewed ? '#8B9D83' : '#C97B63' }}
              >
                {allViewed ? 'Recursos completados' : 'Al completar'}
              </span>
            </div>
            <p className="text-[#F8F6F3]/50 text-xs leading-relaxed">
              {allViewed
                ? 'Ya puedes acceder al cuestionario de evaluación.'
                : 'Consulta todos los recursos para desbloquear la evaluación.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
